import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

const SEO_ROUTES = {
  '/': 'home',
  '/about': 'about',
  '/capacity': 'capacity',
  '/solutions': 'solutions',
  '/products': 'products',
  '/contact': 'contact',
}

const GLOBAL_KEYWORDS = {
  es: [
    'rotomoldeo',
    'rotomoldeo industrial',
    'empresa de rotomoldeo',
    'rotomoldeo de plástico',
    'rotomoldeo de plástico',
    'rotomoldeo en México',
    'rotomoldeo en Mexico',
    'rotomoldeo industrial en México',
    'rotomoldeo industrial en Mexico',
    'empresa de rotomoldeo en México',
    'empresa de rotomoldeo en Mexico',
    'rotomoldeo Monterrey',
    'rotomoldeo Nuevo León',
    'rotomoldeo Nuevo Leon',
    'fabricación de piezas plásticas',
    'fabricacion de piezas plasticas',
    'manufactura de plástico industrial',
    'manufactura de plástico industrial',
    'soluciones de manufactura plástica',
    'soluciones de manufactura plastica',
    'procesos de rotomoldeo',
    'rotomoldeo para la industria',
    'rotomoldeo para jardinería',
    'rotomoldeo para jardineria',
    'rotomoldeo para tarimas',
    'rotomoldeo para contenedores',
    'rotomoldeo para sanitarios portátiles',
    'rotomoldeo para sanitarios portatiles',
    'Macetas rotomoldeo',
    'Macetas Nuevo Leon',
    'moldeo rotacional',
    'fabricante de plástico en Mexico',
  ],
  en: [
    'rotational molding Mexico',
    'rotational molding manufacturer Mexico',
    'plastic manufacturing Mexico',
    'nearshoring plastic manufacturing Mexico',
    'rotational molding process',
    'plastic rotational molding',
    'HDPE rotational molding',
    'industrial plastic molding',
    'industrial rotomolding',
    'custom plastic manufacturing',
    'Mexican plastic manufacturer',
  ],
}

const buildKeywordContent = (pageKeywords, language) => {
  const normalizedLanguage = language === 'en' ? 'en' : 'es'
  const localKeywords = pageKeywords
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)

  const combined = [
    ...localKeywords,
    ...GLOBAL_KEYWORDS.es,
    ...GLOBAL_KEYWORDS.en,
    'Grupo FORMEC',
  ]

  if (normalizedLanguage === 'en') {
    combined.unshift('rotomolding', 'plastic manufacturing', 'Mexico')
  } else {
    combined.unshift('rotomoldeo', 'manufactura plastica', 'Mexico')
  }

  return [...new Set(combined)].join(', ')
}

const DOMAIN_LIKE_SEGMENT = /^([a-z0-9-]+\.)+[a-z]{2,}$/i

const sanitizeBasePath = pathname => {
  const trimmed = (pathname || '').replace(/\/+$/, '')
  if (!trimmed || trimmed === '/') {
    return ''
  }

  const cleanSegments = trimmed
    .split('/')
    .filter(Boolean)
    .filter(segment => !DOMAIN_LIKE_SEGMENT.test(segment))

  return cleanSegments.length ? `/${cleanSegments.join('/')}` : ''
}

const buildRouteUrl = (baseUrl, routePath) => {
  const url = new URL(baseUrl)
  const basePath = url.pathname.replace(/\/+$/, '')
  const normalizedRoutePath = routePath === '/' ? '' : routePath

  url.pathname = `${basePath}${normalizedRoutePath || '/'}`
  url.search = ''

  return url
}

const buildLocalizedUrl = (baseUrl, routePath, language) => {
  const url = buildRouteUrl(baseUrl, routePath)
  url.searchParams.set('lang', language)
  return url.toString()
}

const normalizeRoutePath = pathname => {
  if (!pathname || pathname === '/') {
    return '/'
  }

  const singleSlashPath = pathname.replace(/\/+/g, '/')
  const noTrailingSlash = singleSlashPath.replace(/\/+$/, '')

  return noTrailingSlash || '/'
}

const getBaseUrl = () => {
  const configured = (process.env.REACT_APP_SITE_URL || '').trim()
  if (configured) {
    const candidate = /^https?:\/\//i.test(configured)
      ? configured
      : `https://${configured}`

    try {
      const parsedUrl = new URL(candidate)
      const safePath = sanitizeBasePath(parsedUrl.pathname)
      return `${parsedUrl.origin}${safePath}`
    } catch (error) {
      // Fallback to runtime origin when env URL is malformed.
    }
  }

  if (typeof window !== 'undefined') {
    return window.location.origin.replace(/\/+$/, '')
  }

  return ''
}

const normalizedLanguage = lang => (lang?.startsWith('en') ? 'en' : 'es')

const upsertMeta = (attr, key, content) => {
  if (!content) {
    return
  }

  const selector = `meta[${attr}="${key}"]`
  let element = document.head.querySelector(selector)

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attr, key)
    document.head.appendChild(element)
  }

  element.setAttribute('content', content)
}

const upsertLink = attributes => {
  const { rel, href, hreflang } = attributes
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`
  let element = document.head.querySelector(selector)

  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', rel)
    document.head.appendChild(element)
  }

  if (hreflang) {
    element.setAttribute('hreflang', hreflang)
  } else {
    element.removeAttribute('hreflang')
  }

  element.setAttribute('href', href)
}

const upsertJsonLd = jsonLd => {
  const id = 'formec-organization-jsonld'
  let element = document.head.querySelector(`#${id}`)

  if (!element) {
    element = document.createElement('script')
    element.id = id
    element.type = 'application/ld+json'
    document.head.appendChild(element)
  }

  element.textContent = JSON.stringify(jsonLd)
}

const Seo = () => {
  const location = useLocation()
  const { t, i18n } = useTranslation()

  useEffect(() => {
    const language = normalizedLanguage(i18n.language)
    const path = normalizeRoutePath(location.pathname)
    const pageKey = SEO_ROUTES[path]
    const isPublicRoute = Boolean(pageKey)
    const isRestricted =
      path.startsWith('/dashboard') || path === '/login' || !isPublicRoute
    const safePageKey = pageKey || 'home'
    const siteName = t('seo.siteName')
    const title = t(`seo.pages.${safePageKey}.title`)
    const description = t(`seo.pages.${safePageKey}.description`)
    const keywords = buildKeywordContent(
      t(`seo.pages.${safePageKey}.keywords`),
      language
    )
    const baseUrl = getBaseUrl()
    const canonicalForLang = buildLocalizedUrl(baseUrl, path, language)
    const canonicalEs = buildLocalizedUrl(baseUrl, path, 'es')
    const canonicalEn = buildLocalizedUrl(baseUrl, path, 'en')
    const defaultRouteUrl = buildRouteUrl(baseUrl, path).toString()
    const socialImageUrl = `${baseUrl}/logo512.png`

    document.documentElement.lang = language
    document.title = title

    upsertMeta('name', 'description', description)
    upsertMeta('name', 'keywords', keywords)
    upsertMeta('name', 'author', 'Grupo FORMEC')
    upsertMeta('name', 'publisher', 'Grupo FORMEC')
    upsertMeta('name', 'geo.region', 'MX-NLE')
    upsertMeta('name', 'geo.placename', 'Santa Catarina, Nuevo Leon, Mexico')
    upsertMeta('http-equiv', 'content-language', language)
    upsertMeta('name', 'robots', isRestricted ? 'noindex, nofollow' : 'index, follow')

    upsertMeta('property', 'og:type', 'website')
    upsertMeta('property', 'og:site_name', siteName)
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:description', description)
    upsertMeta('property', 'og:url', canonicalForLang)
    upsertMeta('property', 'og:image', socialImageUrl)
    upsertMeta('property', 'og:image:alt', siteName)
    upsertMeta(
      'property',
      'og:locale',
      language === 'es' ? 'es_MX' : 'en_US'
    )
    upsertMeta(
      'property',
      'og:locale:alternate',
      language === 'es' ? 'en_US' : 'es_MX'
    )

    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:title', title)
    upsertMeta('name', 'twitter:description', description)
    upsertMeta('name', 'twitter:image', socialImageUrl)

    upsertLink({ rel: 'canonical', href: canonicalForLang })

    if (isRestricted) {
      ;['es', 'en', 'x-default'].forEach(code => {
        const element = document.head.querySelector(
          `link[rel="alternate"][hreflang="${code}"]`
        )
        if (element) {
          element.remove()
        }
      })
    } else {
      upsertLink({ rel: 'alternate', hreflang: 'es', href: canonicalEs })
      upsertLink({ rel: 'alternate', hreflang: 'en', href: canonicalEn })
      upsertLink({ rel: 'alternate', hreflang: 'x-default', href: defaultRouteUrl })
    }

    upsertJsonLd({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteName,
      url: baseUrl || undefined,
      email: 'info@grupoformec.com',
      telephone: '+52 818 336 2016',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'MX',
        addressRegion: 'Nuevo Leon',
        addressLocality: 'Santa Catarina',
        streetAddress:
          'Carr. Monterrey-Saltillo 3061 Fracc. Bosques del Poniente 66362',
      },
    })
  }, [i18n.language, location.pathname, t])

  return null
}

export default Seo